/**
 * Field references in control parameters.
 *
 * A FormDown control parameter may name a field instead of carrying a value:
 *
 *   {geo:route from=[Mitarbeiter.wohnortGeo] to=[einsatzort]}
 *
 * - `[einsatzort]` is a field of the document the control is bound to.
 * - `[Mitarbeiter.wohnortGeo]` is the field `wohnortGeo` of the document reached
 *   through the form's Relation named `Mitarbeiter` (`relatedDocuments`, matched by
 *   `data.name`). Every segment before the field is a capitalised Relation name, the
 *   same rule resolve paths use (`Mitarbeiter -> data.wohnortGeo`).
 *
 * The control still receives the literal text (`"[einsatzort]"`) in `params`, so
 * controls that ignore references keep working. A control that wants the value calls
 * `watchFieldParam(name, callback)`; this module turns the text into a value and
 * keeps delivering it:
 *
 * - a local field follows the live edit state (unsaved changes included);
 * - a Relation of type RESOLVE whose `resolve` path is a single `data.<field>` step
 *   follows that reference field live, so changing the reference in the editor
 *   re-resolves before anything is saved;
 * - every other Relation resolves from the stored document (`Aeppic.resolveAll`)
 *   and again whenever the bound document's version changes. A document that was
 *   never saved resolves to `null`.
 *
 * A parameter that is not a reference is delivered as it is, so a design may pass a
 * value (`:params="{ from: someGeoLocation }"`) where a form passes a reference.
 * Only references to the bound document's own fields are writable.
 */

import type * as Types from '@aeppic/types'

/** A parsed `[field]` / `[Relation.field]` reference */
export interface FieldReference {
  /** The text as written, e.g. `[Mitarbeiter.wohnortGeo]` */
  raw: string
  /** All segments, e.g. `['Mitarbeiter', 'wohnortGeo']` */
  path: string[]
  /** The field at the end, e.g. `wohnortGeo` */
  field: string
  /** Relation names before the field, e.g. `['Mitarbeiter']`; empty for a local field */
  relations: string[]
}

export type FieldParamInfo =
  | { kind: 'literal', value: unknown }
  | { kind: 'field', reference: FieldReference, field: string, writable: boolean }
  | { kind: 'relation', reference: FieldReference, field: string, relations: string[], writable: false }

export interface FieldParamDelivery {
  /** The reference behind the value, or null when the parameter is a plain value */
  reference: FieldReference | null
  /** Set when resolving a Relation failed; the value is then null */
  error?: unknown
}

export type FieldParamCallback = (value: any, delivery: FieldParamDelivery) => void

type Unwatch = () => void

/** What `watchFieldParam` needs from the control it runs in */
export interface FieldParamHost {
  Aeppic: {
    get(identifier: any): Promise<Types.Document | null>
    getAll(identifiers: any[]): Promise<(Types.Document | null)[]>
    resolveAll(start: Types.IdOrReference, path: string): Promise<(Types.Document | null)[]>
  }
  getDocument(): Types.Document | null | undefined
  getParam(name: string): unknown
  isReadonly(): boolean
  /** Vue's `vm.$watch` */
  $watch(getter: () => any, callback: (value: any, oldValue: any) => void, options?: { deep?: boolean, immediate?: boolean }): Unwatch
}

const SEGMENT = '[A-Za-z_][A-Za-z0-9_]*'
const REFERENCE = new RegExp(`^\\[(${SEGMENT}(?:\\.${SEGMENT})*)\\]$`)
const SINGLE_DATA_STEP = new RegExp(`^data\\.(${SEGMENT})$`)

/**
 * Parses `[field]` or `[Relation.field]`. Returns null for anything else, including
 * a lowercase dotted path such as `[mitarbeiter.field]`, which has no meaning.
 */
export function parseFieldReference(value: unknown): FieldReference | null {
  if (typeof value !== 'string') {
    return null
  }

  const match = REFERENCE.exec(value.trim())

  if (!match) {
    return null
  }

  const path = match[1].split('.')
  const relations = path.slice(0, -1)

  if (relations.some(segment => !/^[A-Z]/.test(segment))) {
    return null
  }

  return { raw: value, path, field: path[path.length - 1], relations }
}

/** Describes what a parameter value refers to, without resolving it */
export function describeFieldParam(value: unknown): FieldParamInfo {
  const reference = parseFieldReference(value)

  if (!reference) {
    return { kind: 'literal', value }
  }

  if (reference.relations.length === 0) {
    return { kind: 'field', reference, field: reference.field, writable: true }
  }

  return { kind: 'relation', reference, field: reference.field, relations: reference.relations, writable: false }
}

/**
 * Delivers the value a parameter refers to, now and whenever it changes.
 * Returns a function that stops watching.
 */
export function watchFieldParam(host: FieldParamHost, name: string, callback: FieldParamCallback): Unwatch {
  let stopBinding: Unwatch | null = null
  let boundRaw: unknown = Symbol('unbound')

  const bind = (raw: unknown) => {
    if (raw === boundRaw) {
      return
    }

    boundRaw = raw
    stopBinding?.()
    stopBinding = null

    const reference = parseFieldReference(raw)

    if (!reference) {
      callback(raw, { reference: null })
      return
    }

    if (reference.relations.length === 0) {
      stopBinding = host.$watch(
        () => host.getDocument()?.data?.[reference.field],
        value => callback(value ?? null, { reference }),
        { deep: true, immediate: true },
      )
      return
    }

    stopBinding = watchRelationField(host, reference, (value, error) => {
      callback(value, error === undefined ? { reference } : { reference, error })
    })
  }

  const stopParam = host.$watch(() => host.getParam(name), raw => bind(raw))
  bind(host.getParam(name))

  return () => {
    stopParam()
    stopBinding?.()
    stopBinding = null
  }
}

/**
 * Writes the field a `[field]` parameter refers to. Only fields of the bound document
 * can be written; a Relation reference or a plain value throws.
 */
export function setFieldParam(host: FieldParamHost, name: string, value: unknown): void {
  const info = describeFieldParam(host.getParam(name))

  if (info.kind !== 'field') {
    throw new Error(info.kind === 'relation'
      ? `Parameter '${name}' refers to a field of a related document (${info.reference.raw}); only fields of this document can be written`
      : `Parameter '${name}' is not a field reference`)
  }

  if (host.isReadonly()) {
    return
  }

  const document = host.getDocument()

  if (!document?.data) {
    return
  }

  document.data[info.field] = value
}

/* ------------------------------------------------------------------------------------------------ */

function watchRelationField(host: FieldParamHost, reference: FieldReference, done: (value: any, error?: unknown) => void): Unwatch {
  const { Aeppic } = host
  const [first, ...rest] = reference.relations
  let stopped = false
  let sequence = 0
  let stopInner: Unwatch | null = null

  // Only the latest resolution may deliver; older ones finish silently
  const run = async (compute: () => Promise<any>) => {
    const mine = ++sequence

    try {
      const value = await compute()

      if (!stopped && mine === sequence) {
        done(value)
      }
    } catch (error) {
      if (!stopped && mine === sequence) {
        done(null, error)
      }
    }
  }

  const fieldOf = (documents: (Types.Document | null)[]) => {
    const values = documents.filter(Boolean).map(document => document.data?.[reference.field] ?? null)
    return values.length === 0 ? null : values.length === 1 ? values[0] : values
  }

  // Follows the remaining relations from one start document and reads the field
  const resolveFrom = async (start: Types.IdOrReference, relations: string[]) => {
    if (relations.length === 0) {
      return [await Aeppic.get(start)]
    }

    return Aeppic.resolveAll(start, relations.join(' -> '))
  }

  const setup = async () => {
    const document = host.getDocument()

    if (!document) {
      done(null)
      return
    }

    const relation = await findRelation(Aeppic, document, first)

    if (stopped) {
      return
    }

    const step = relation?.data?.type === 'RESOLVE' && typeof relation.data.resolve === 'string'
      ? SINGLE_DATA_STEP.exec(relation.data.resolve.trim())
      : null

    if (step) {
      // Live: the Relation follows a reference field of this document
      const referenceField = step[1]

      stopInner = host.$watch(
        () => host.getDocument()?.data?.[referenceField],
        refValue => run(async () => {
          const starts = (Array.isArray(refValue) ? refValue : [refValue]).filter(isReferenceLike)
          const documents = (await Promise.all(starts.map(start => resolveFrom(start, rest)))).flat()
          return fieldOf(documents)
        }),
        { deep: true, immediate: true },
      )
      return
    }

    // Stored: resolve the whole path from the saved document, again after each save
    stopInner = host.$watch(
      () => host.getDocument()?.v,
      () => run(async () => {
        const current = host.getDocument()
        return current ? fieldOf(await resolveFrom(current.id, reference.relations)) : null
      }),
      { immediate: true },
    )
  }

  setup().catch(error => {
    if (!stopped) {
      done(null, error)
    }
  })

  return () => {
    stopped = true
    stopInner?.()
    stopInner = null
  }
}

async function findRelation(Aeppic: FieldParamHost['Aeppic'], document: Types.Document, name: string) {
  const formId = (document as any).f?.id

  if (!formId) {
    return null
  }

  // Same lookup as resolve paths: the latest version of the document's form
  const formDocument = await Aeppic.get(formId)
  const related = formDocument?.data?.relatedDocuments

  if (!Array.isArray(related) || related.length === 0) {
    return null
  }

  const relations = await Aeppic.getAll(related)
  return relations.find(relation => relation?.data?.name === name) ?? null
}

function isReferenceLike(value: unknown): value is Types.IdOrReference {
  if (typeof value === 'string') {
    return value.length > 0
  }

  return !!value && typeof value === 'object' && typeof (value as any).id === 'string' && (value as any).id !== ''
}
